/**
 * Storage module for TitCock.tv
 *
 * Handles all localStorage operations for persisting:
 * - User preferences (mute state, etc.)
 * - Liked videos with thumbnails
 * - Other persistent data
 */

import { playerState, likesState } from "./state.js";

// ==========================================================================
// Storage Keys
// ==========================================================================

const STORAGE_KEYS = {
  GLOBAL_MUTED: "globalMuted",
  LIKED_SLUGS: "likedSlugsV1",
  LIKED_VIDEOS_DATA: "likedVideosDataV1",
};

// ==========================================================================
// Load Functions
// ==========================================================================

/**
 * Load all persisted state from localStorage
 */
export const loadState = () => {
  loadMuteState();
  loadLikedVideos();
};

/**
 * Load mute state from localStorage
 */
export const loadMuteState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.GLOBAL_MUTED);
    playerState.globalMuted = stored !== null ? JSON.parse(stored) : true;
  } catch {
    playerState.globalMuted = true;
  }
};

/**
 * Load liked videos from localStorage
 */
export const loadLikedVideos = () => {
  try {
    // Load slugs set
    const slugsJson = localStorage.getItem(STORAGE_KEYS.LIKED_SLUGS);
    const slugsArray = slugsJson ? JSON.parse(slugsJson) : [];
    likesState.slugs = new Set(slugsArray);

    // Load full video data
    const dataJson = localStorage.getItem(STORAGE_KEYS.LIKED_VIDEOS_DATA);
    const dataArray = dataJson ? JSON.parse(dataJson) : [];
    likesState.videosData = new Map(dataArray);

    // Preload thumbnails
    for (const data of likesState.videosData.values()) {
      if (data.thumbnail) {
        const img = new Image();
        img.referrerPolicy = "no-referrer";
        img.src = data.thumbnail;
      }
    }
  } catch {
    likesState.slugs = new Set();
    likesState.videosData = new Map();
  }
};

// ==========================================================================
// Save Functions
// ==========================================================================

/**
 * Save mute state to localStorage
 */
export const saveMuteState = () => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.GLOBAL_MUTED,
      JSON.stringify(playerState.globalMuted),
    );
  } catch (e) {
    console.warn("Failed to save mute state:", e);
  }
};

/**
 * Save liked videos to localStorage
 */
export const saveLikedVideos = () => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.LIKED_SLUGS,
      JSON.stringify([...likesState.slugs]),
    );
    // Save all data without stripping thumbnails
    const allData = [...likesState.videosData];

    localStorage.setItem(
      STORAGE_KEYS.LIKED_VIDEOS_DATA,
      JSON.stringify(allData),
    );
  } catch (e) {
    console.warn("Failed to save liked videos:", e);
  }
};

// ==========================================================================
// Liked Videos Operations
// ==========================================================================

/**
 * Check if a video is liked
 * @param {string} slug - Video slug
 * @returns {boolean}
 */
export const isLiked = (slug) => {
  return likesState.slugs.has(slug);
};

/**
 * Check if a card element is liked
 * @param {HTMLElement} card - Card element
 * @returns {boolean}
 */
export const isCardLiked = (card) => {
  const slug = card?.dataset?.slug;
  return slug ? isLiked(slug) : false;
};

/**
 * Save a liked video with all its data
 * @param {string} slug - Video slug
 * @param {Object} data - Video data object
 * @param {string} data.name
 * @param {string} data.category
 * @param {string} data.categoryLabel
 * @param {string} data.title
 * @param {string} data.author
 * @param {number} data.likes
 * @param {string} data.redgifsUrl
 * @param {string} data.videoSrc
 * @param {string} data.thumbnail - RedGifs thumbnail URL
 */
export const saveLikedVideo = (slug, data) => {
  likesState.slugs.add(slug);
  likesState.videosData.set(slug, {
    slug,
    name: data.name || "",
    category: data.category || "",
    categoryLabel: data.categoryLabel || "",
    title: data.title || "",
    author: data.author || "",
    likes: Number(data.likes) || 0,
    redgifsUrl: data.redgifsUrl || "",
    videoSrc: data.videoSrc || "",
    thumbnail: data.thumbnail || "",
  });
  saveLikedVideos();
};

/**
 * Remove a liked video
 * @param {string} slug - Video slug
 */
export const removeLikedVideo = (slug) => {
  likesState.slugs.delete(slug);
  likesState.videosData.delete(slug);
  saveLikedVideos();
};

/**
 * Toggle like status for a video
 * @param {string} slug - Video slug
 * @param {Object} [data] - Video data (required if liking)
 * @returns {boolean} New like status
 */
export const toggleLike = (slug, data) => {
  if (isLiked(slug)) {
    removeLikedVideo(slug);
    return false;
  } else if (data) {
    saveLikedVideo(slug, data);
    return true;
  }
  return false;
};

/**
 * Get all liked video slugs
 * @returns {string[]}
 */
export const getLikedSlugs = () => {
  return [...likesState.slugs];
};

/**
 * Get all liked video data
 * @returns {Map}
 */
export const getLikedVideosData = () => {
  return likesState.videosData;
};

/**
 * Get data for a specific liked video
 * @param {string} slug
 * @returns {Object|undefined}
 */
export const getLikedVideoData = (slug) => {
  return likesState.videosData.get(slug);
};

/**
 * Get the count of liked videos
 * @returns {number}
 */
export const getLikedCount = () => {
  return likesState.slugs.size;
};

// ==========================================================================
// Storage Utilities
// ==========================================================================

/**
 * Clear all stored data (use with caution!)
 */
export const clearAllData = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.GLOBAL_MUTED);
    localStorage.removeItem(STORAGE_KEYS.LIKED_SLUGS);
    localStorage.removeItem(STORAGE_KEYS.LIKED_VIDEOS_DATA);

    // Reset state
    playerState.globalMuted = true;
    likesState.slugs = new Set();
    likesState.videosData = new Map();
  } catch (e) {
    console.warn("Failed to clear data:", e);
  }
};

/**
 * Get approximate storage usage for liked videos
 * @returns {Object} { count, sizeKB }
 */
export const getStorageStats = () => {
  try {
    const dataJson =
      localStorage.getItem(STORAGE_KEYS.LIKED_VIDEOS_DATA) || "[]";
    const sizeBytes = new Blob([dataJson]).size;
    return {
      count: likesState.slugs.size,
      sizeKB: Math.round(sizeBytes / 1024),
    };
  } catch {
    return { count: 0, sizeKB: 0 };
  }
};
